import React, { useMemo, useState } from 'react';
import {
  Flex,
  Spinner,
  Text,
  Box,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  Icon,
  Button,
  Link,
  Collapse,
  Divider,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  Warning,
  Buildings,
  FilePdf,
  XCircle,
  Info,
  ListBullets,
  ArrowSquareOut,
  CaretDown,
  CaretUp,
} from 'phosphor-react';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useDebounceCallback } from 'usehooks-ts';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import { IDataNoPagination } from '../../../../types/shared';
import {
  IEfdErrosEmpresa,
  IEfdErroDetalhe,
  IEfdErrosKpi,
  IEfdErrosCategoria,
  IEfdErrosAgrupados,
  useEfdErros,
} from '../../../../hooks/useEfdErros';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { PageBox } from '../../../../components/pageBox';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';

moment.locale('pt-br');

const CATEGORIA_LABELS: Record<string, string> = {
  // Erros de validacao (PDF)
  RECEITAS_CREDITOS: 'Receitas/Créditos',
  REGISTRO_OBRIGATORIO: 'Registro Obrigatório',
  REGISTRO_M105_M505: 'Registro M105/M505',
  CALCULO_DIVERGENTE: 'Cálculo Divergente',
  CST_CREDITO: 'CST/Crédito',
  PRAZO_ENTREGA: 'Prazo de Entrega',
  REGISTRO_NAO_OBRIGATORIO: 'Registro Não Obrigatório',
  ESTRUTURA: 'Estrutura',
  // Erros de processamento (RPA)
  VALIDACAO_ARQUIVO: 'Validação do Arquivo',
  ERRO_RELATORIO: 'Erro no Relatório',
  ERRO_IMPORTACAO: 'Erro na Importação',
  SOMENTE_VISUALIZACAO: 'Somente Visualização',
  ERRO_PROCURACAO: 'Erro de Procuração',
  TIMEOUT_IMPORTACAO: 'Timeout Importação',
  TIMEOUT_TRANSMISSAO: 'Timeout Transmissão',
  ERRO_BANCO_DADOS: 'Erro Banco de Dados',
  NENHUMA_ESCRITURACAO: 'Nenhuma Escrituração',
  ERRO_EXCLUSAO: 'Erro na Exclusão',
  ERRO_RPA: 'Erro RPA',
  OUTROS: 'Outros',
};

const CATEGORIA_COLORS: Record<string, string> = {
  RECEITAS_CREDITOS: 'orange',
  REGISTRO_OBRIGATORIO: 'red',
  REGISTRO_M105_M505: 'purple',
  CALCULO_DIVERGENTE: 'pink',
  CST_CREDITO: 'cyan',
  PRAZO_ENTREGA: 'yellow',
  REGISTRO_NAO_OBRIGATORIO: 'gray',
  ESTRUTURA: 'teal',
  VALIDACAO_ARQUIVO: 'orange',
  ERRO_RELATORIO: 'red',
  ERRO_IMPORTACAO: 'red',
  SOMENTE_VISUALIZACAO: 'blue',
  ERRO_PROCURACAO: 'red',
  TIMEOUT_IMPORTACAO: 'yellow',
  TIMEOUT_TRANSMISSAO: 'yellow',
  ERRO_BANCO_DADOS: 'red',
  NENHUMA_ESCRITURACAO: 'gray',
  ERRO_EXCLUSAO: 'red',
  ERRO_RPA: 'red',
  OUTROS: 'gray',
};

const CATEGORIA_BG: Record<string, string> = {
  RECEITAS_CREDITOS: '#FFF3E0',
  REGISTRO_OBRIGATORIO: '#FFEBEE',
  REGISTRO_M105_M505: '#F3E5F5',
  CALCULO_DIVERGENTE: '#FCE4EC',
  CST_CREDITO: '#E0F7FA',
  PRAZO_ENTREGA: '#FFFDE7',
  REGISTRO_NAO_OBRIGATORIO: '#ECEFF1',
  ESTRUTURA: '#E0F2F1',
  VALIDACAO_ARQUIVO: '#FFF3E0',
  ERRO_RELATORIO: '#FFEBEE',
  ERRO_IMPORTACAO: '#FFEBEE',
  SOMENTE_VISUALIZACAO: '#E3F2FD',
  ERRO_PROCURACAO: '#FFEBEE',
  TIMEOUT_IMPORTACAO: '#FFFDE7',
  TIMEOUT_TRANSMISSAO: '#FFFDE7',
  ERRO_BANCO_DADOS: '#FFEBEE',
  NENHUMA_ESCRITURACAO: '#ECEFF1',
  ERRO_EXCLUSAO: '#FFEBEE',
  ERRO_RPA: '#FFEBEE',
  OUTROS: '#F5F5F5',
};

const CATEGORIA_ICON_COLOR: Record<string, string> = {
  RECEITAS_CREDITOS: '#E65100',
  REGISTRO_OBRIGATORIO: '#C62828',
  REGISTRO_M105_M505: '#6A1B9A',
  CALCULO_DIVERGENTE: '#AD1457',
  CST_CREDITO: '#00838F',
  PRAZO_ENTREGA: '#F9A825',
  REGISTRO_NAO_OBRIGATORIO: '#546E7A',
  ESTRUTURA: '#00695C',
  VALIDACAO_ARQUIVO: '#E65100',
  ERRO_RELATORIO: '#C62828',
  ERRO_IMPORTACAO: '#C62828',
  SOMENTE_VISUALIZACAO: '#1565C0',
  ERRO_PROCURACAO: '#C62828',
  TIMEOUT_IMPORTACAO: '#F9A825',
  TIMEOUT_TRANSMISSAO: '#F9A825',
  ERRO_BANCO_DADOS: '#C62828',
  NENHUMA_ESCRITURACAO: '#546E7A',
  ERRO_EXCLUSAO: '#C62828',
  ERRO_RPA: '#C62828',
  OUTROS: '#757575',
};

interface EmpresaCardProps {
  empresa: IEfdErrosEmpresa;
}

function EmpresaCard({ empresa }: EmpresaCardProps) {
  const [expanded, setExpanded] = useState(empresa.erros.length <= 2);
  const categorias = Array.from(new Set(empresa.erros.map(e => e.erro_categoria || 'OUTROS')));
  const primaryColor = CATEGORIA_ICON_COLOR[categorias[0] || 'OUTROS'] || '#757575';

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px"
      borderColor="gray.200"
      borderLeftWidth="4px"
      borderLeftColor={primaryColor}
      p={3}
      transition="all 0.2s"
      _hover={{ boxShadow: 'sm' }}
      minW={0}
      w="100%"
    >
      {/* Header */}
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="xs" fontWeight="600" color="gray.500">
          ID: {empresa.empresa_id}
        </Text>
        <HStack spacing={1} flexWrap="wrap" justify="flex-end">
          {categorias.map(cat => (
            <Badge key={cat} colorScheme={CATEGORIA_COLORS[cat] || 'gray'} fontSize="2xs">
              {CATEGORIA_LABELS[cat] || cat}
            </Badge>
          ))}
        </HStack>
      </Flex>

      {/* Nome da Empresa */}
      <Flex align="center" gap={1.5} mb={1} minW={0}>
        <Icon as={Buildings} color="blue.500" boxSize={4} flexShrink={0} />
        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={2} minW={0}>
          {empresa.contribuinte || '-'}
        </Text>
      </Flex>

      {/* CNPJ + Periodo */}
      <Flex justify="space-between" align="center" mb={2}>
        {empresa.cnpj && (
          <Text fontSize="xs" color="gray.500">
            CNPJ: {empresa.cnpj}
          </Text>
        )}
        <Text fontSize="2xs" color="gray.400">
          {empresa.periodo_inicio} a {empresa.periodo_fim}
        </Text>
      </Flex>

      {/* Resumo */}
      <Flex
        align="center"
        justify="space-between"
        bg="gray.50"
        p={2}
        borderRadius="md"
        mb={2}
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        _hover={{ bg: 'gray.100' }}
      >
        <HStack spacing={2}>
          <Badge colorScheme="red" variant="solid" fontSize="xs" px={2}>
            {empresa.erros.length}
          </Badge>
          <Text fontSize="xs" fontWeight="600" color="gray.700">
            {empresa.erros.length === 1 ? 'pendência' : 'pendências'}
          </Text>
          <Text fontSize="2xs" color="gray.500">
            ({empresa.total_pendencias} ocorrências)
          </Text>
        </HStack>
        {expanded ? <CaretUp size={16} color="#718096" /> : <CaretDown size={16} color="#718096" />}
      </Flex>

      {/* Lista de Erros */}
      <Collapse in={expanded} animateOpacity>
        <VStack spacing={2} align="stretch">
          {empresa.erros.map((erro: IEfdErroDetalhe, idx: number) => {
            const cat = erro.erro_categoria || 'OUTROS';
            const bgColor = erro.tipo_pendencia === 'erro' ? 'red.50' : 'orange.50';

            return (
              <Box key={erro.id} w="100%">
                {idx > 0 && <Divider mb={2} />}
                <Box bg={bgColor} p={2} borderRadius="md">
                  {/* Badge do tipo + categoria */}
                  <HStack spacing={1} mb={1.5}>
                    <Badge
                      colorScheme={erro.tipo_pendencia === 'erro' ? 'red' : 'yellow'}
                      fontSize="2xs"
                    >
                      {erro.tipo_pendencia === 'erro' ? 'Erro' : 'Aviso'}
                    </Badge>
                    <Badge colorScheme={CATEGORIA_COLORS[cat] || 'gray'} fontSize="2xs">
                      {CATEGORIA_LABELS[cat] || cat}
                    </Badge>
                    {erro.erro_quantidade != null && erro.erro_quantidade > 1 && (
                      <Badge colorScheme="gray" fontSize="2xs" variant="subtle">
                        {erro.erro_quantidade}x
                      </Badge>
                    )}
                  </HStack>

                  {/* Descricao completa */}
                  <Text fontSize="xs" color="gray.700">
                    {erro.erro_descricao}
                  </Text>

                  {/* Link PDF */}
                  {erro.s3_url && (
                    <Link href={erro.s3_url} isExternal mt={1.5} display="inline-block">
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        leftIcon={<FilePdf size={14} />}
                      >
                        Ver PDF
                        <ArrowSquareOut size={12} style={{ marginLeft: 4 }} />
                      </Button>
                    </Link>
                  )}
                </Box>
              </Box>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
}

interface KpiCardsProps {
  kpi: IEfdErrosKpi;
  isLoading: boolean;
  categoriaFilter: string;
  tipoPendenciaFilter: string;
  // eslint-disable-next-line no-unused-vars
  onCategoriaFilterChange: (cat: string) => void;
  // eslint-disable-next-line no-unused-vars
  onTipoPendenciaFilterChange: (tipo: string) => void;
}

function KpiCards({
  kpi,
  isLoading,
  categoriaFilter,
  tipoPendenciaFilter,
  onCategoriaFilterChange,
  onTipoPendenciaFilterChange,
}: KpiCardsProps) {
  const handleTopCardClick = (tipo: string) => {
    onCategoriaFilterChange('');
    onTipoPendenciaFilterChange(tipoPendenciaFilter === tipo ? '' : tipo);
  };
  return (
    <Box mb={6}>
      <SimpleGrid columns={{ base: 2, xl: 4 }} spacing={{ base: 2, md: 4 }} mb={4}>
        {/* Total */}
        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => {
            onCategoriaFilterChange('');
            onTipoPendenciaFilterChange('');
          }}
          border={!categoriaFilter && !tipoPendenciaFilter ? '2px solid' : '1px solid'}
          borderColor={!categoriaFilter && !tipoPendenciaFilter ? 'blue.400' : 'gray.200'}
          _hover={{ borderColor: 'blue.300' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#E3F2FD" flexShrink={0}>
                <ListBullets color="#1976D2" size={18} />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Total Pendências
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.total_pendencias}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>

        {/* Erros */}
        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => handleTopCardClick('erro')}
          border={tipoPendenciaFilter === 'erro' ? '2px solid' : '1px solid'}
          borderColor={tipoPendenciaFilter === 'erro' ? 'red.400' : 'gray.200'}
          _hover={{ borderColor: 'red.300' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#FFEBEE" flexShrink={0}>
                <XCircle color="#E53935" size={18} weight="fill" />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Erros
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.total_erros}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>

        {/* Avisos */}
        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => handleTopCardClick('aviso')}
          border={tipoPendenciaFilter === 'aviso' ? '2px solid' : '1px solid'}
          borderColor={tipoPendenciaFilter === 'aviso' ? 'yellow.400' : 'gray.200'}
          _hover={{ borderColor: 'yellow.300' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#FFF8E1" flexShrink={0}>
                <Info color="#F9A825" size={18} weight="fill" />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Avisos
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.total_avisos}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>

        {/* Empresas */}
        <Card shadow="sm" borderRadius="lg">
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#E3F2FD" flexShrink={0}>
                <HiOutlineOfficeBuilding color="#1976D2" size={18} />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Empresas
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.total_empresas}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Cards de categorias */}
      {!isLoading && kpi.categorias.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={{ base: 2, md: 3 }}>
          {kpi.categorias.map((cat: IEfdErrosCategoria) => (
            <Card
              key={cat.categoria}
              shadow="sm"
              borderRadius="lg"
              cursor="pointer"
              onClick={() =>
                onCategoriaFilterChange(categoriaFilter === cat.categoria ? '' : cat.categoria)
              }
              border={categoriaFilter === cat.categoria ? '2px solid' : '1px solid'}
              borderColor={
                categoriaFilter === cat.categoria
                  ? `${CATEGORIA_COLORS[cat.categoria] || 'gray'}.400`
                  : 'gray.200'
              }
              _hover={{
                borderColor: `${CATEGORIA_COLORS[cat.categoria] || 'gray'}.300`,
              }}
              transition="all 0.2s"
            >
              <CardBody py={2} px={3}>
                <Flex align="center" gap={2}>
                  <Box
                    p={1.5}
                    borderRadius="md"
                    bg={CATEGORIA_BG[cat.categoria] || '#F5F5F5'}
                    flexShrink={0}
                  >
                    <Warning
                      color={CATEGORIA_ICON_COLOR[cat.categoria] || '#757575'}
                      size={16}
                      weight="fill"
                    />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text fontSize="2xs" color="gray.500" fontWeight="medium" noOfLines={1}>
                      {CATEGORIA_LABELS[cat.categoria] || cat.categoria}
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800">
                        {cat.quantidade}
                      </Text>
                      <Text fontSize="2xs" color="gray.400">
                        {cat.empresas} emp.
                      </Text>
                    </HStack>
                  </Box>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
}

export function EfdErrosPage() {
  const { getErrosAgrupados, getKpi } = useEfdErros();

  const {
    currentDate: currentFilterDate,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters: monthFilters,
    setFilters: setMonthFilters,
  } = useMonthFilter({ defaultToPreviousMonth: true });

  const dateStart = useMemo(
    () => moment(monthFilters.get('date_start')).startOf('month').format('YYYY-MM-DD'),
    [monthFilters],
  );

  const [searchMode, setSearchMode] = useState<'Nome' | 'ID' | 'CNPJ'>('ID');
  const [searchText, setSearchText] = useState(
    () =>
      monthFilters.get('search') ||
      monthFilters.get('external_id') ||
      monthFilters.get('cnpj') ||
      '',
  );
  const [categoriaFilter, setCategoriaFilter] = useState('');
  const [tipoPendenciaFilter, setTipoPendenciaFilter] = useState('');

  const debouncedSearch = useDebounceCallback((value: string) => {
    const newFilters = new URLSearchParams(monthFilters);
    newFilters.delete('search');
    newFilters.delete('external_id');
    newFilters.delete('cnpj');
    if (value) {
      if (searchMode === 'ID') {
        newFilters.set('external_id', value);
      } else if (searchMode === 'CNPJ') {
        newFilters.set('cnpj', value.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      } else {
        newFilters.set('search', value);
      }
    }
    setMonthFilters(newFilters);
    resetPage();
  }, 500);

  const handleCategoriaFilter = (cat: string) => {
    setCategoriaFilter(cat);
    if (cat) setTipoPendenciaFilter('');
    resetPage();
  };

  const [page, setPage] = useState(1);
  const pageSize = 20;

  const filtersForQuery = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(monthFilters)]);
    return cleanFilters({
      ...baseFilters,
      categoria: categoriaFilter || undefined,
      tipo_pendencia: tipoPendenciaFilter || undefined,
      date_start: dateStart,
      page,
      page_size: pageSize,
    });
  }, [monthFilters, categoriaFilter, tipoPendenciaFilter, dateStart, page]);

  const kpiFilters = useMemo(() => cleanFilters({ date_start: dateStart }), [dateStart]);

  // Reset pagina quando filtros mudam
  const resetPage = () => setPage(1);

  const { data: dataEmpresas, isFetching } = useQuery<IDataNoPagination<IEfdErrosAgrupados>>({
    queryKey: ['getEfdErrosAgrupados', filtersForQuery],
    queryFn: getErrosAgrupados,
  });

  const { data: kpiData, isFetching: isFetchingKpi } = useQuery<IDataNoPagination<IEfdErrosKpi>>({
    queryKey: ['getEfdErrosKpi', kpiFilters],
    queryFn: getKpi,
  });

  const empresas = dataEmpresas?.data?.items ?? [];
  const totalEmpresas = dataEmpresas?.data?.total ?? 0;
  const totalPages = Math.ceil(totalEmpresas / pageSize);
  const kpi = kpiData?.data ?? {
    total_pendencias: 0,
    total_erros: 0,
    total_avisos: 0,
    total_empresas: 0,
    categorias: [],
  };

  return (
    <PageBox>
      <div className="flex flex-col gap-2">
        {/* Header */}
        <div className="flex items-center justify-between pb-5">
          <div className="flex content-start lg:content-center">
            <ReturnButton previousPage="modulesHome/supervisor" />
            <div className="h-full">
              <div
                className="flex items-center justify-center rounded-full p-3"
                style={{ backgroundColor: '#FFF3E0' }}
              >
                <Warning color="#E65100" className="size-8" />
              </div>
            </div>
            <div className="flex w-full flex-col justify-center gap-1 pl-2">
              <Text fontWeight="bold" fontSize="16px">
                AGENTE EFD CONTRIBUIÇÕES
              </Text>
              <Text fontSize="12px" color="gray.500">
                Pendências de validação detectadas na transmissão do EFD-Contribuições
              </Text>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <KpiCards
          kpi={kpi}
          isLoading={isFetchingKpi}
          categoriaFilter={categoriaFilter}
          tipoPendenciaFilter={tipoPendenciaFilter}
          onCategoriaFilterChange={handleCategoriaFilter}
          onTipoPendenciaFilterChange={(tipo: string) => {
            setTipoPendenciaFilter(tipo);
            resetPage();
          }}
        />

        {/* Filters */}
        <Box mb={4} p={4} bg="gray.50" borderRadius="lg">
          <Flex align="flex-end" gap={3} flexWrap="wrap">
            <Box flex={1} minW="300px">
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                BUSCAR
              </Text>
              <HStack spacing={2}>
                <SearchModeBar
                  modes={MODES_ID_NOME_CNPJ}
                  mode={searchMode}
                  onModeChange={m => setSearchMode(m as 'Nome' | 'ID' | 'CNPJ')}
                  value={searchText}
                  onValueChange={v => {
                    setSearchText(v);
                    debouncedSearch(v);
                  }}
                />
              </HStack>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                MÊS DE REFERÊNCIA
              </Text>
              <MonthNavigator
                currentDate={currentFilterDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
            </Box>
          </Flex>
        </Box>

        {/* Cards Grid */}
        {isFetching && (
          <Flex justify="center" align="center" h="400px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        )}

        {!isFetching && empresas.length === 0 && (
          <Flex justify="center" align="center" h="400px" direction="column" gap={4}>
            <Warning size={48} color="#9CA3AF" />
            <Text color="gray.500">Nenhum resultado encontrado para este período</Text>
          </Flex>
        )}

        {!isFetching && empresas.length > 0 && (
          <>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {empresas.map(empresa => (
                <EmpresaCard key={empresa.empresa_id} empresa={empresa} />
              ))}
            </SimpleGrid>

            {/* Paginacao */}
            {totalPages > 1 && (
              <Flex justify="center" align="center" mt={6} gap={3}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  isDisabled={page <= 1}
                >
                  Anterior
                </Button>
                <Text fontSize="sm" color="gray.600">
                  Página {page} de {totalPages} ({totalEmpresas} empresas)
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  isDisabled={page >= totalPages}
                >
                  Próxima
                </Button>
              </Flex>
            )}
          </>
        )}
      </div>
    </PageBox>
  );
}
